'use strict';

// Product-page Wheel Gallery (WI-30): lays out the curated PostAgent gallery
// as justified rows and provides the full-gallery viewer.
//
// Data: snippets/dha-product-wheel-gallery.liquid emits the published
// `images_manifest` (the complete, ordered gallery — Liquid cannot loop a
// file list past 50, the manifest is how up to 128 images reach the page) as
// a JSON island. Each image carries its real width/height, so rows are sized
// from true aspect ratios: mixed portrait/landscape photography is never
// cropped or letterboxed, and nothing shifts when images load.
//
// Order: the explicit hero leads, then PostAgent's published order. The
// inline section shows a bounded number of rows; "+N" and "View all" open the
// viewer, which always reaches every image.

(function () {
  if (window.DHAProductGallery) return;

  var NOTE = 'Gallery photos may show different finishes. Every wheel is made to order in the finish you choose.';

  function toNum(value) {
    var n = Number(value);
    return isFinite(n) && n > 0 ? n : 0;
  }

  function aspectOf(image) {
    var w = toNum(image.width);
    var h = toNum(image.height);
    return w && h ? w / h : 1.5;
  }

  // Shopify's CDN resizes any file URL via ?width= — the published originals
  // are several thousand pixels wide and must never be sent as-is.
  function sized(url, width) {
    if (!url) return '';
    return url + (url.indexOf('?') < 0 ? '?' : '&') + 'width=' + Math.round(width);
  }

  // Hero first, then the published order with the hero removed from its slot.
  function displayOrder(images, heroIndex) {
    var out = images.map(function (image, position) { return { image: image, position: position }; });
    if (typeof heroIndex === 'number' && heroIndex > 0 && heroIndex < out.length) {
      out.unshift(out.splice(heroIndex, 1)[0]);
    }
    return out;
  }

  // Justified rows: add photos until the row would be at least as wide as the
  // container at the target height, then scale the row so it fills the width
  // exactly. The first row targets a larger height so the lead photos read
  // larger. `leadAlone` (narrow screens) puts the lead photo on its own row,
  // never taller than `maxLeadH`. A final row that cannot fill the width is
  // left at the target height instead of being stretched.
  function computeRows(ars, width, gap, target, leadTarget, maxRows, opts) {
    var rows = [];
    var n = ars.length;
    var i = 0;
    opts = opts || {};
    while (i < n && rows.length < maxRows) {
      var first = rows.length === 0;
      if (first && opts.leadAlone) {
        var lh = Math.min(width / ars[0], opts.maxLeadH || width);
        rows.push({ start: 0, end: 1, h: lh });
        i = 1;
        continue;
      }
      var t = first ? leadTarget : target;
      var start = i;
      var sum = 0;
      while (i < n) {
        sum += ars[i];
        i++;
        if (sum * t + gap * (i - start - 1) >= width) break;
      }
      var count = i - start;
      var filled = sum * t + gap * (count - 1) >= width;
      var h = (width - gap * (count - 1)) / sum;
      if (!filled) h = Math.min(h, t);
      rows.push({ start: start, end: i, h: h, fill: filled ? 1 : (sum * h + gap * (count - 1)) / width });
    }
    return rows;
  }

  // A complete gallery whose last row can't fill the width would end in a
  // ragged gap. Nudge the target row height (within a modest band) to the
  // value that fills the last row best; a truncated preview always ends on a
  // full row, so it keeps the nominal height.
  function bestRows(ars, width, gap, target, leadRatio, maxRows, opts) {
    var nominal = computeRows(ars, width, gap, target, Math.round(target * leadRatio), maxRows, opts);
    var last = nominal[nominal.length - 1];
    if (!last || last.end < ars.length || last.fill >= 0.88) return nominal;
    var best = nominal;
    var bestScore = (1 - last.fill) + 0;
    for (var f = 0.92; f <= 1.2501; f += 0.02) {
      var t = target * f;
      var rows = computeRows(ars, width, gap, t, Math.round(t * leadRatio), maxRows, opts);
      var tail = rows[rows.length - 1];
      var score = (1 - tail.fill) + 1.2 * Math.abs(f - 1);
      if (tail.end >= ars.length && score < bestScore) {
        best = rows;
        bestScore = score;
      }
    }
    return best;
  }

  function el(tag, className, attrs) {
    var node = document.createElement(tag);
    if (className) node.className = className;
    if (attrs) Object.keys(attrs).forEach(function (key) { node.setAttribute(key, attrs[key]); });
    return node;
  }

  // The full-screen photo viewer (WI-30), shared: the product page's Wheel Gallery and the 305 Forged
  // landing page (WI-68) both build theirs here. Everything is created on first open and lives in
  // <body>; one viewer per photo list.
  //
  //   createViewer({
  //     items:       [{ url, alt }, ...]  the complete, ordered list (url without a width; sized() adds it)
  //     ariaLabel:   the dialog's accessible name
  //     className:   an extra class on the dialog for a variant's own styling (optional)
  //     title:       the text at the top left (the wheel code, the series name)
  //     quoteLabel:  text of the Request a Quote button; leave out for no button
  //     note:        small text under the photo; leave out for none
  //   }) -> { open(index, indexView, opener), close(restoreFocus), count }
  //
  // open() moves focus into the viewer and close(true) returns it to `opener`.
  function createViewer(opts) {
    var items = opts.items || [];
    var total = items.length;
    var viewer = null;
    var lastFocus = null;

    var current = 0;
    var indexBuilt = false;

    function buildViewer() {
      var quoteLabel = opts.quoteLabel;
      viewer = el('div', 'dha-pgv' + (opts.className ? ' ' + opts.className : ''), { role: 'dialog', 'aria-modal': 'true', 'aria-label': opts.ariaLabel });
      viewer.hidden = true;
      viewer.innerHTML =
        '<div class="dha-pgv__bar">' +
          '<div class="dha-pgv__id"><span class="dha-pgv__code"></span><span class="dha-pgv__count" aria-live="polite"></span></div>' +
          '<div class="dha-pgv__actions">' +
            '<button type="button" class="dha-pgv__btn" data-pgv-index aria-pressed="false"></button>' +
            (quoteLabel ? '<button type="button" class="dha-pgv__btn dha-pgv__btn--quote" data-dha-quote-trigger></button>' : '') +
            '<button type="button" class="dha-pgv__btn" data-pgv-close aria-label="Close gallery">Close ✕</button>' +
          '</div>' +
        '</div>' +
        '<div class="dha-pgv__stage">' +
          '<button type="button" class="dha-pgv__nav" data-pgv-prev aria-label="Previous photo">←</button>' +
          '<figure class="dha-pgv__figure"><img alt="" decoding="async"></figure>' +
          '<button type="button" class="dha-pgv__nav" data-pgv-next aria-label="Next photo">→</button>' +
        '</div>' +
        '<div class="dha-pgv__index" hidden><div class="dha-pgv__index-grid"></div></div>' +
        (opts.note ? '<p class="dha-pgv__foot"></p>' : '');
      viewer.querySelector('.dha-pgv__code').textContent = opts.title || '';
      if (quoteLabel) viewer.querySelector('[data-dha-quote-trigger]').textContent = quoteLabel;
      if (opts.note) viewer.querySelector('.dha-pgv__foot').textContent = opts.note;
      document.body.appendChild(viewer);

      viewer.addEventListener('click', function (event) {
        var t = event.target.closest('button');
        if (!t) return;
        if (t.hasAttribute('data-pgv-close')) closeViewer(true);
        else if (t.hasAttribute('data-pgv-prev')) step(-1);
        else if (t.hasAttribute('data-pgv-next')) step(1);
        else if (t.hasAttribute('data-pgv-index')) setIndexView(!isIndexView());
        else if (t.hasAttribute('data-dha-quote-trigger')) closeViewer(false); // the quote flow opens above, via its own delegate
      });

      var figure = viewer.querySelector('.dha-pgv__figure');
      var startX = null;
      var startY = null;
      figure.addEventListener('touchstart', function (event) {
        if (event.touches.length !== 1) return;
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      }, { passive: true });
      figure.addEventListener('touchend', function (event) {
        if (startX === null) return;
        var dx = event.changedTouches[0].clientX - startX;
        var dy = event.changedTouches[0].clientY - startY;
        startX = startY = null;
        if (Math.abs(dx) > 48 && Math.abs(dx) > Math.abs(dy) * 1.4) step(dx < 0 ? 1 : -1);
      }, { passive: true });
    }

    function isIndexView() {
      return !viewer.querySelector('.dha-pgv__index').hidden;
    }

    function buildIndex() {
      if (indexBuilt) return;
      indexBuilt = true;
      var host = viewer.querySelector('.dha-pgv__index-grid');
      items.forEach(function (item, i) {
        var thumb = el('button', 'dha-pgv__thumb', { type: 'button', 'aria-label': 'Photo ' + (i + 1) + ' of ' + total });
        var img = el('img', '', { alt: '', loading: 'lazy', decoding: 'async', src: sized(item.url, 360) });
        thumb.appendChild(img);
        thumb.addEventListener('click', function () {
          show(i);
          setIndexView(false);
        });
        host.appendChild(thumb);
      });
    }

    function setIndexView(on) {
      var idxEl = viewer.querySelector('.dha-pgv__index');
      var stage = viewer.querySelector('.dha-pgv__stage');
      if (on) buildIndex();
      idxEl.hidden = !on;
      stage.hidden = on;
      var toggle = viewer.querySelector('[data-pgv-index]');
      toggle.setAttribute('aria-pressed', on ? 'true' : 'false');
      toggle.textContent = on ? 'Single view' : 'Index · ' + total;
      if (on) {
        var thumbs = viewer.querySelectorAll('.dha-pgv__thumb');
        for (var i = 0; i < thumbs.length; i++) {
          if (i === current) thumbs[i].setAttribute('aria-current', 'true');
          else thumbs[i].removeAttribute('aria-current');
        }
        if (thumbs[current]) thumbs[current].scrollIntoView({ block: 'center' });
        var focusTarget = thumbs[current] || viewer.querySelector('[data-pgv-index]');
        focusTarget.focus();
      } else {
        viewer.querySelector('[data-pgv-next]').focus();
      }
    }

    function preload(i) {
      var item = items[(i + total) % total];
      if (!item) return;
      var img = new Image();
      img.src = sized(item.url, 2000);
    }

    function show(i) {
      current = (i + total) % total;
      var item = items[current];
      var img = viewer.querySelector('.dha-pgv__figure img');
      img.alt = item.alt;
      img.src = sized(item.url, 2000);
      viewer.querySelector('.dha-pgv__count').textContent = String(current + 1).padStart(2, '0') + ' / ' + String(total).padStart(2, '0');
      var multiple = total > 1;
      viewer.querySelector('[data-pgv-prev]').hidden = !multiple;
      viewer.querySelector('[data-pgv-next]').hidden = !multiple;
      if (multiple) {
        preload(current + 1);
        preload(current - 1);
      }
    }

    function step(delta) {
      if (total < 2) return;
      show(current + delta);
    }

    function focusables() {
      return Array.prototype.filter.call(
        viewer.querySelectorAll('button, [href], [tabindex]:not([tabindex="-1"])'),
        function (node) { return !node.hidden && node.offsetParent !== null; }
      );
    }

    function onKey(event) {
      if (!viewer || viewer.hidden) return;
      if (document.body.classList.contains('dha-quote-modal-open')) return;
      if (event.key === 'Escape') {
        event.preventDefault();
        closeViewer(true);
      } else if (event.key === 'ArrowRight' && !isIndexView()) {
        step(1);
      } else if (event.key === 'ArrowLeft' && !isIndexView()) {
        step(-1);
      } else if (event.key === 'Tab') {
        var list = focusables();
        if (!list.length) return;
        var first = list[0];
        var last = list[list.length - 1];
        if (event.shiftKey && document.activeElement === first) {
          event.preventDefault();
          last.focus();
        } else if (!event.shiftKey && document.activeElement === last) {
          event.preventDefault();
          first.focus();
        } else if (!viewer.contains(document.activeElement)) {
          event.preventDefault();
          first.focus();
        }
      }
    }

    function openViewer(index, indexView, opener) {
      if (!viewer) {
        buildViewer();
        document.addEventListener('keydown', onKey);
      }
      lastFocus = opener || document.activeElement;
      viewer.hidden = false;
      document.body.classList.add('dha-pgv-open');
      show(index);
      var toggle = viewer.querySelector('[data-pgv-index]');
      toggle.textContent = 'Index · ' + total;
      toggle.hidden = total < 2;
      setIndexView(!!indexView && total > 1);
      if (!indexView || total < 2) viewer.querySelector('[data-pgv-close]').focus();
    }

    function closeViewer(restoreFocus) {
      if (!viewer || viewer.hidden) return;
      viewer.hidden = true;
      document.body.classList.remove('dha-pgv-open');
      if (restoreFocus && lastFocus && typeof lastFocus.focus === 'function') lastFocus.focus();
    }

    return { open: openViewer, close: closeViewer, count: total };
  }

  function mount(root, data) {
    if (!root || !data || !data.images || !data.images.length) return null;

    var code = data.wheel_code || data.code || '';
    var order = displayOrder(data.images, data.hero_index);
    var total = order.length;
    var items = order.map(function (entry, n) {
      var image = entry.image;
      var base = image.alt || (code ? code + ' wheel' : 'Wheel photo');
      return {
        url: image.url,
        ar: aspectOf(image),
        alt: base + ' — photo ' + (n + 1) + ' of ' + total,
      };
    }).filter(function (item) { return !!item.url; });
    total = items.length;
    if (!total) return null;

    var grid = root.querySelector('[data-dha-pg-grid]');

    // ── Inline justified rows ───────────────────────────────────────────

    var tiles = [];
    var loadedWidth = [];

    function tileFor(index) {
      if (tiles[index]) return tiles[index];
      var item = items[index];
      var tile = el('button', 'dha-pg__tile', { type: 'button', 'aria-label': 'Open photo ' + (index + 1) + ' of ' + total + ' in the gallery' });
      tile.style.setProperty('--ar', item.ar.toFixed(4));
      tile.setAttribute('data-index', String(index));
      var img = el('img', '', { alt: item.alt, loading: 'lazy', decoding: 'async' });
      tile.appendChild(img);
      tile.addEventListener('click', function () {
        if (tile.getAttribute('data-more') === 'true') openViewer(0, true, tile);
        else openViewer(index, false, tile);
      });
      tiles[index] = tile;
      return tile;
    }

    function setTileSize(index, tile, w, h) {
      tile.style.width = Math.round(w) + 'px';
      tile.style.height = Math.round(h) + 'px';
      var want = Math.min(1800, Math.ceil((w * Math.min(window.devicePixelRatio || 1, 2)) / 200) * 200);
      if (!loadedWidth[index] || want > loadedWidth[index]) {
        loadedWidth[index] = want;
        tile.firstChild.src = sized(items[index].url, want);
      }
    }

    function layout() {
      if (!grid) return;
      var width = Math.floor(grid.clientWidth);
      if (width < 200) return;
      var narrow = width < 640;
      var gap = width < 768 ? 8 : 10;
      var target = width >= 1000 ? 240 : width >= 640 ? 200 : 168;
      // A small gallery shows everything; a larger one a bounded preview
      // (the viewer always reaches the rest).
      var maxRows = total <= 12 ? total : narrow ? 4 : 3;
      var rows = bestRows(
        items.map(function (item) { return item.ar; }),
        width, gap, target, narrow ? 1 : 1.4, maxRows,
        { leadAlone: narrow, maxLeadH: Math.round(width * 0.9) }
      );

      grid.classList.remove('dha-pg__grid--static');
      grid.textContent = '';
      var shown = rows.length ? rows[rows.length - 1].end : 0;
      var remaining = total - shown;

      rows.forEach(function (row, rowIndex) {
        var rowEl = el('div', 'dha-pg__row' + (narrow && rowIndex === 0 && row.end - row.start === 1 ? ' dha-pg__row--center' : ''));
        for (var i = row.start; i < row.end; i++) {
          var tile = tileFor(i);
          var w = items[i].ar * row.h;
          setTileSize(i, tile, w, row.h);
          var isLast = remaining > 0 && rowIndex === rows.length - 1 && i === row.end - 1;
          var existing = tile.querySelector('.dha-pg__more');
          if (existing) existing.parentNode.removeChild(existing);
          tile.classList.toggle('dha-pg__tile--more', isLast);
          if (isLast) {
            tile.setAttribute('data-more', 'true');
            tile.setAttribute('aria-label', 'View all ' + total + ' photos');
            var more = el('span', 'dha-pg__more', { 'aria-hidden': 'true' });
            var count = el('span', 'dha-pg__more-count');
            count.textContent = '+' + remaining;
            var label = el('span', 'dha-pg__more-label');
            label.textContent = 'View all ' + total;
            more.appendChild(count);
            more.appendChild(label);
            tile.appendChild(more);
          } else {
            tile.removeAttribute('data-more');
            tile.setAttribute('aria-label', 'Open photo ' + (i + 1) + ' of ' + total + ' in the gallery');
          }
          rowEl.appendChild(tile);
        }
        grid.appendChild(rowEl);
      });
    }

    var raf = 0;
    var lastWidth = 0;
    function scheduleLayout() {
      if (raf) return;
      raf = window.requestAnimationFrame(function () {
        raf = 0;
        var w = grid ? Math.floor(grid.clientWidth) : 0;
        if (w === lastWidth) return;
        lastWidth = w;
        layout();
      });
    }

    if (grid) {
      lastWidth = Math.floor(grid.clientWidth);
      layout();
      if (typeof ResizeObserver === 'function') new ResizeObserver(scheduleLayout).observe(grid);
      else window.addEventListener('resize', scheduleLayout);
    }

    // ── Viewer ──────────────────────────────────────────────────────────
    // The viewer itself is createViewer() above (shared with the 305 Forged landing page).

    var viewerApi = createViewer({
      items: items,
      ariaLabel: (code ? code + ' ' : '') + 'wheel gallery',
      title: code,
      quoteLabel: root.getAttribute('data-quote-label') || 'Request a Quote',
      note: root.getAttribute('data-note') || NOTE,
    });

    function openViewer(index, indexView, opener) {
      viewerApi.open(index, indexView, opener);
    }

    Array.prototype.forEach.call(root.querySelectorAll('[data-dha-pg-open]'), function (button) {
      button.textContent = (total > 10 ? 'View all ' + total : 'Open gallery') + ' →';
      button.addEventListener('click', function () { openViewer(0, total > 10, button); });
    });

    return { layout: layout, open: openViewer, close: viewerApi.close, itemCount: total };
  }

  function init() {
    var roots = document.querySelectorAll('[data-dha-pg]');
    Array.prototype.forEach.call(roots, function (root) {
      var island = root.querySelector('script[data-dha-pg-data]');
      if (!island) return;
      try {
        mount(root, JSON.parse(island.textContent));
      } catch (error) {
        if (window.console && console.warn) console.warn('DHA Wheel Gallery: could not read the gallery data.', error);
      }
    });
  }

  window.DHAProductGallery = { mount: mount, createViewer: createViewer, computeRows: computeRows, bestRows: bestRows, displayOrder: displayOrder, sized: sized, aspectOf: aspectOf };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();
})();
